import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Package, Shield, MapPin, Plus, ChevronDown, User, Users, UserCog, Lock } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import { Footer } from '../components/Footer';


const SAMPLE = [
  { name: '1 bag · 8 kg', bags: 1, weight: 8 },
  { name: '2 bags · 12 kg + 18 kg', bags: 2, weight: 30 },
  { name: '3 bags · 25 kg total', bags: 3, weight: 25 },
];

const calc = (wt) => {
  const subtotal = 500 + wt * 65;
  const gst = subtotal * 0.18;
  const total = subtotal + gst;
  return { subtotal, gst, total };
};

const Pricing = () => {
  const navigate = useNavigate();
  const [weight, setWeight] = useState(15);
  // Empty-string state allows the user to fully backspace the input. We treat
  // an empty / non-numeric value as zero for calculation purposes.
  const numericWeight = weight === '' || isNaN(Number(weight)) ? 0 : Math.max(0, Math.min(50, Number(weight)));
  const breakdown = calc(numericWeight);
  const [signInOpen, setSignInOpen] = useState(false);

  return (
    <div className="theme-sunset" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Top Bar */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#E8DCC9]/85 border-b border-[#1A1A1A]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="back-to-home" className="flex items-center">
            <BrandMark size={36} textClass="text-base sm:text-lg text-[#1A1A1A]" />
          </Link>
          <nav className="hidden sm:flex items-center gap-7 text-xs uppercase tracking-[0.2em] text-[#525252]">
            <Link to="/" data-testid="nav-home" className="hover:text-[#FF5A1F] transition-colors">Home</Link>
            <Link to="/pricing" className="text-[#FF5A1F] border-b border-[#FF5A1F] pb-0.5">Pricing</Link>
            <Link to="/faq" className="hover:text-[#FF5A1F] transition-colors">FAQ</Link>
            <Link to="/track" className="hover:text-[#FF5A1F] transition-colors">Track</Link>
            <Link
              to="/?signin=1"
              data-testid="nav-signin-btn"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1A1A1A] text-white hover:bg-[#FF5A1F] transition-colors text-xs font-bold tracking-wide no-underline"
            >
              <User size={13} /> Sign In
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-20 pb-12">
        <div className="relative z-10">
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#FF5A1F] mb-6 font-semibold">── Pricing</div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] text-[#1A1A1A]">
            One price.<br />
            <span className="text-[#FF5A1F]">No surprises.</span>
          </h1>
          <p className="mt-6 text-lg max-w-2xl leading-relaxed text-[#525252]">
            We don&apos;t do tiers, surge pricing or hidden fees. Just one transparent formula —
            a flat base fare plus a per-kilogram charge. GST included up-front.
          </p>
        </div>
      </section>

      {/* Formula card — bento split */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-6 pb-20">
        {/* The Formula */}
        <div className="md:col-span-7 rounded-3xl bg-white/92 backdrop-blur border border-[#1A1A1A]/10 p-10 md:p-14 shadow-sm">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5A1F] font-semibold">
            The Formula
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4 items-start">
            <div>
              <div className="text-5xl md:text-6xl font-bold tracking-tighter leading-none text-[#1A1A1A]">₹500</div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-[#525252]">Base Fare</div>
              <div className="mt-1 text-xs text-[#737373]">Per booking</div>
            </div>
            <div className="pt-2 flex justify-center">
              <div className="w-12 h-12 rounded-full bg-[#FF5A1F] flex items-center justify-center">
                <Plus size={22} className="text-white" />
              </div>
            </div>
            <div>
              <div className="text-5xl md:text-6xl font-bold tracking-tighter leading-none text-[#1A1A1A]">₹65</div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-[#525252]">Per Kg</div>
              <div className="mt-1 text-xs text-[#737373]">Each bag</div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-[#1A1A1A]/10">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF5A1F] font-semibold">+18% GST</div>
            <p className="mt-2 text-sm text-[#525252] leading-relaxed">
              Goods &amp; Services Tax is added to the subtotal at checkout. Receipt provided.
            </p>
          </div>
        </div>

        {/* Live calculator */}
        <div className="md:col-span-5 rounded-3xl bg-[#1A1A1A] p-10 md:p-12 flex flex-col text-white">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5A1F] mb-6 font-bold">
            Calculator
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/65">Total weight</label>
            <div className="flex items-baseline gap-3 mt-2">
              <input
                type="number"
                min={0}
                max={50}
                value={weight}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') { setWeight(''); return; }
                  const n = Number(v);
                  if (Number.isNaN(n)) return;
                  setWeight(Math.max(0, Math.min(50, n)));
                }}
                data-testid="pricing-weight-input"
                className="text-5xl md:text-6xl font-bold tracking-tighter w-32 outline-none"
                style={{ background: 'transparent', border: 'none', borderBottom: '2px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 0, padding: '0 0 4px 0' }}
              />
              <span className="text-2xl font-semibold text-white/70">kg</span>
            </div>
          </div>
          <div className="mt-auto pt-8 space-y-2 text-sm text-white/85">
            <div className="flex justify-between"><span>Base</span><span>₹500</span></div>
            <div className="flex justify-between"><span>{numericWeight}kg × ₹65</span><span>₹{(numericWeight*65).toFixed(0)}</span></div>
            <div className="flex justify-between border-t border-white/15 pt-2"><span className="text-white/65">Subtotal</span><span>₹{breakdown.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-white/65">GST 18%</span><span>₹{breakdown.gst.toFixed(2)}</span></div>
            <div className="flex justify-between text-2xl font-bold pt-3 border-t border-white/30">
              <span className="text-white">TOTAL</span>
              <span className="text-[#FF5A1F]">₹{breakdown.total.toFixed(0)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sample rates */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-10 text-[#1A1A1A]">Real-world examples</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {SAMPLE.map((s, i) => {
            const r = calc(s.weight);
            return (
              <div key={i} className="rounded-2xl bg-white/92 backdrop-blur border border-[#1A1A1A]/10 p-8 hover:border-[#FF5A1F]/40 hover:shadow-md transition-all">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#737373]">0{i + 1}</div>
                <div className="mt-3 text-lg font-semibold text-[#1A1A1A]">{s.name}</div>
                <div className="mt-6 text-4xl font-bold tracking-tighter text-[#FF5A1F]">
                  ₹{r.total.toFixed(0)}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-[#737373]">Incl. GST</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-white/80 backdrop-blur border-y border-[#1A1A1A]/10 py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { Icon: Shield, t: 'Insured up to ₹10,000', d: 'Every booking is covered against loss or damage.' },
            { Icon: MapPin, t: 'Live tracking', d: 'See exactly where your luggage is, in real time.' },
            { Icon: Package, t: 'OTP-verified delivery', d: 'Bags handed over only after you share the OTP.' },
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#FF5A1F] flex items-center justify-center flex-shrink-0">
                <b.Icon className="text-white" size={22} />
              </div>
              <div>
                <div className="font-semibold text-base text-[#1A1A1A]">{b.t}</div>
                <div className="text-sm text-[#525252] mt-1 leading-relaxed">{b.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-8 py-24 text-center">
        <h2 className="relative text-4xl md:text-5xl font-bold tracking-tight text-[#1A1A1A]">
          Ready when you are.
        </h2>
        <button
          data-testid="pricing-book-now-btn"
          onClick={() => navigate('/?book=1')}
          className="mt-8 inline-flex items-center gap-2 bg-[#FF5A1F] text-white px-9 py-4 rounded-full text-sm font-bold uppercase tracking-widest hover:bg-[#E64200] hover:scale-[1.02] transition-all shadow-[0_8px_24px_rgba(255,90,31,0.3)]"
        >
          Book a pickup <ChevronRight size={18} />
        </button>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
